import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconCodeBlock from '@apollo/icons/default/IconCodeBlock.svg';
import IconFields from '@apollo/icons/default/IconFields.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import IconTable from '@apollo/icons/default/IconTable.svg';
import {
  Alert,
  Button,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import { colors } from '@apollo/space-kit/colors';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import { omit, uniqueId } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';

import { TraceProtobuf } from 'src/app/graph/operationsPage/report-pane/section-report/traces-section/queryTraces/histogramTraceFinder/queryTraceSection/traceProtobuf/TraceProtobuf';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ReadonlyEditor } from 'src/components/editor/ReadonlyEditor';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { useMermaidRender } from 'src/components/queryPlan/mermaid/Mermaid';
import {
  QueryPlan,
  QueryPlanRenderer,
  useQueryPlanFromEditor,
} from 'src/components/queryPlan/QueryPlan';
import { queryPlanToMermaid } from 'src/components/queryPlan/queryPlanToMermaid';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { SubgraphAndMetadata } from 'src/lib/graphql/subgraphMetadataHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { SplitPane } from '../../../../components/common/splitPane/SplitPane';
import { useThemeContext } from '../../../../components/themeProvider/ThemeProvider';
import { Reachability } from '../../hooks/useEndpointReachability';
import { GraphRef } from '../../hooks/useGraphRef';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import { useAnalyticsContext } from '../hooks/useAnalyticsContext';
import { isPotentialCORSError } from '../hooks/useExplorerState/performOperation/performOperation';
import { ExplorerResult } from '../hooks/useExplorerState/useExplorerResponses';
import { useExplorerState } from '../hooks/useExplorerState/useExplorerState';
import { IntrospectionFailure } from '../hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';

import { ConnectorsResponsePane } from './connectorsResponsePane/ConnectorsResponsePane';
import { ExplorerResultsTab, ExplorerResultsTabs } from './ExplorerResultsTab';
import { ResponsePane } from './responsePane/ResponsePane';
import { ResultStatus } from './resultStatus/ResultStatus';
import { SubscriptionResponsePane } from './subscriptionResponsePane/SubscriptionResponsePane';
import { useNetworkErrorToast } from './useNetworkErrorToast';

interface ResultsPaneProps {
  operationResult: ExplorerResult;
  resultsTab: ExplorerResultsTab;
  setResultsTab: (tab: ExplorerResultsTab) => void;
  endpointReachability: Reachability;
  introspectionFailure: IntrospectionFailure | undefined;
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
  subscription: ReturnType<typeof useExplorerState>['subscription'];
  setShowMermaidQueryPlan: (value: boolean) => void;
  showMermaidQueryPlan: boolean;
  setIsTableMode: (value: boolean) => void;
  isTableMode: boolean;
  isMockingResponses: boolean;
  endpointUrl: string | null;
  subscriptionUrl: string | null;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  showTraces: boolean;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  hasSupergraphSchema: boolean;
  accountId: string | undefined;
  graphRef: GraphRef | null;
  subgraphMap?: Record<string, SubgraphAndMetadata>;
}
export const ResultsPane = ({
  operationResult,
  resultsTab,
  setResultsTab,
  endpointReachability,
  introspectionFailure,
  openEditUrlModal,
  subscription,
  setShowMermaidQueryPlan,
  showMermaidQueryPlan,
  setIsTableMode,
  isTableMode,
  isMockingResponses,
  endpointUrl,
  subscriptionUrl,
  explorerBehavioralFlags,
  showTraces,
  editor,
  schema,
  hasSupergraphSchema,
  accountId,
  graphRef,
  subgraphMap,
}: ResultsPaneProps) => {
  const [shouldShowSubscriptionResponses, setShouldShowSubscriptionResponses] =
    useState(false);

  useEffect(() => {
    // Auto open the subscription panel if the panel is closed and we're listening for responses
    if (subscription.isListening) {
      setShouldShowSubscriptionResponses(true);
    }
  }, [subscription.isListening]);

  const [subscriptionHeight, setSubscriptionHeight] = useLocalStorage(
    'studioSubscriptionHeight',
  );

  const { track } = useAnalyticsContext();

  const { themeName, themedValue } = useThemeContext();

  const stableToastId = React.useMemo(() => uniqueId(), []);

  const [showNetworkWarning, setShowNetworkWarning] = React.useState(false);
  const { showNetworkErrorToast, hideNetworkErrorToast } = useNetworkErrorToast(
    {
      setShowNetworkWarning,
      openEditUrlModal,
      triggeredBySubscription: false,
      introspectionDisabled: introspectionFailure === 'introspectionDisabled',
      url: endpointUrl,
    },
  );

  const {
    data: { graphVariantPermissions },
  } = usePermissions(accountId, graphRef?.graphId, graphRef?.graphVariant);

  useEffect(() => {
    // TODO (maya): show specific copy in network tab when we know why we can't reach your endpoint
    // `possibleCorsError` = pingable endpoint, bad response
    // `unreachable` = nonsense endpoint
    // `introspectionDisabled` = introspection disabled (show this in the network card)
    setShowNetworkWarning(
      endpointReachability !== 'reachable' &&
        endpointReachability !== 'unknown',
    );
  }, [endpointReachability]);

  useEffect(() => {
    if (
      operationResult?.type === 'error' &&
      !explorerBehavioralFlags.shouldPostMessageOperations
    ) {
      setShowNetworkWarning(isPotentialCORSError(operationResult));
    }
  }, [explorerBehavioralFlags.shouldPostMessageOperations, operationResult]);

  useEffect(() => {
    if (resultsTab === 'Response' && showNetworkWarning) {
      showNetworkErrorToast();
    } else {
      hideNetworkErrorToast();
    }
  }, [
    openEditUrlModal,
    themeName,
    resultsTab,
    showNetworkWarning,
    stableToastId,
    introspectionFailure,
    themedValue,
    showNetworkErrorToast,
    hideNetworkErrorToast,
  ]);

  const lastResult =
    operationResult?.type === 'loading'
      ? operationResult.previousResponse
      : operationResult;

  const showTableViewBtns = useMemo(() => {
    if (resultsTab !== 'Response' || lastResult?.type !== 'response')
      return false;
    // we only want to show the table view btns if we have a response that
    // can be formatted, the table view structure relies on body.data to exist
    if (
      isMockingResponses &&
      lastResult.data.body &&
      !lastResult.data.body.data
    ) {
      return false;
    }
    return true;
  }, [lastResult, isMockingResponses, resultsTab]);

  const showQueryPlanBtns =
    resultsTab === 'Query Plan Preview' || resultsTab === 'Query Plan';

  const displayedTabs = ExplorerResultsTabs.filter((tab) => {
    const hasResponse = lastResult?.type === 'response';
    switch (tab) {
      case 'Query Plan Preview':
        return (
          hasSupergraphSchema && graphVariantPermissions?.canQueryBuildConfig
        );
      case 'Query Plan':
        return lastResult?.type === 'response' && lastResult.data.queryPlan;
      case 'Traces':
        return showTraces && hasResponse;
      case 'Response Headers':
        return hasResponse && explorerBehavioralFlags.showResponseHeaders;
      case 'Connectors Debugger':
        return (
          hasResponse && !!lastResult?.data?.connectorsDebugging?.data?.length
        );
      default:
        return true;
    }
  });

  useEffect(() => {
    // Push user to an appropriate tab if their current tab is not in the `displayedTabs` list
    if (!displayedTabs.includes(resultsTab)) {
      setResultsTab(displayedTabs[0] || 'Response');
    }
  }, [displayedTabs, resultsTab, setResultsTab]);

  const responseOperationName =
    lastResult?.type === 'response' ? lastResult.operationName : undefined;

  const queryPlanResponseFromEditor = useQueryPlanFromEditor({
    hasSupergraphSchema,
    operationEditor: editor,
    schema,
  });

  const queryPlanContent = useMemo(
    () =>
      resultsTab === 'Query Plan Preview'
        ? queryPlanResponseFromEditor.state === 'success'
          ? queryPlanResponseFromEditor
          : null
        : lastResult?.type === 'response'
          ? lastResult.data.queryPlan
          : null,
    [lastResult, queryPlanResponseFromEditor, resultsTab],
  );

  const connectorsDebuggingContent =
    lastResult?.type === 'response'
      ? lastResult.data.connectorsDebugging
      : null;

  const mermaidPlanNodeString = useMemo(
    () =>
      queryPlanContent?.object
        ? queryPlanToMermaid(queryPlanContent.object)
        : '',
    [queryPlanContent?.object],
  );
  const queryPlanSVGText = useMermaidRender(mermaidPlanNodeString);

  return (
    <div className="flex size-full flex-col pr-2">
      <div className="mb-2 mt-4 flex flex-none items-center justify-between gap-2">
        {displayedTabs.length > 1 ? (
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="hidden"
              rightIcon={<IconChevronDown />}
            >
              {resultsTab}
            </MenuButton>
            <MenuContent>
              <MenuList>
                {displayedTabs.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    onClick={() => {
                      track(`Tab to`, { label: option });
                      setResultsTab(option as ExplorerResultsTab);
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuContent>
          </Menu>
        ) : (
          <div
            // extra css to match the look of the Select button above
            className="font-semibold text-primary"
            role="heading"
            aria-level={2}
          >
            Response
          </div>
        )}

        {showTableViewBtns && (
          <>
            <Tooltip label="Show response as JSON">
              <IconButton
                aria-label="Turn JSON mode on"
                isActive={!isTableMode}
                size="sm"
                variant="hidden"
                icon={<IconCodeBlock />}
                onClick={() => setIsTableMode(false)}
              />
            </Tooltip>
            <Tooltip label="Show response in table view">
              <IconButton
                aria-label="Turn table mode on"
                isActive={isTableMode}
                size="sm"
                variant="hidden"
                icon={<IconTable />}
                onClick={() => setIsTableMode(true)}
              />
            </Tooltip>
          </>
        )}
        {showQueryPlanBtns && (
          <>
            <Tooltip label="Show plan as chart">
              <IconButton
                aria-label="Turn on chart mode"
                isActive={showMermaidQueryPlan}
                size="sm"
                variant="hidden"
                icon={<IconFields />}
                onClick={() => setShowMermaidQueryPlan(true)}
              />
            </Tooltip>
            <Tooltip label="Show plan as text">
              <IconButton
                aria-label="Turn on text mode"
                isActive={!showMermaidQueryPlan}
                size="sm"
                variant="hidden"
                icon={<IconCodeBlock />}
                onClick={() => setShowMermaidQueryPlan(false)}
              />
            </Tooltip>
            <Tooltip label="Open query plan in a new window">
              <IconButton
                aria-label="Open query plan SVG in a new window"
                isDisabled={!queryPlanSVGText}
                size="sm"
                variant="hidden"
                onClick={() => {
                  if (queryPlanSVGText)
                    window.open(
                      URL.createObjectURL(
                        new Blob([queryPlanSVGText], {
                          type: 'image/svg+xml',
                        }),
                      ),
                    );
                }}
                icon={<IconOutlink />}
              />
            </Tooltip>
          </>
        )}

        <span className="flex-1" />

        {explorerBehavioralFlags.showResultStatus && (
          <ResultStatus
            operationResult={operationResult}
            isMockingResponses={isMockingResponses}
          />
        )}
      </div>
      <div
        className={classnames(
          // Exclude the response and response headers from fullstory
          'fs-exclude flex size-full flex-1 flex-col overflow-hidden',
          operationResult?.type === 'loading' && 'opacity-50',
        )}
        data-testid="result_pane"
      >
        <CursorProvider>
          {resultsTab === 'Response' &&
            (shouldShowSubscriptionResponses ? (
              <SplitPane
                split="horizontal"
                primary="second" // make the bottom pane the primary pane
                minSize={200}
                maxSize={-80} // top pane is never smaller than 100px
                size={subscriptionHeight}
                onChange={setSubscriptionHeight}
              >
                <ResponsePane
                  lastResult={lastResult}
                  isTableMode={isTableMode}
                  responseName={responseOperationName}
                  explorerBehavioralFlags={explorerBehavioralFlags}
                />
                <SubscriptionResponsePane
                  openEditUrlModal={openEditUrlModal}
                  subscription={subscription}
                  subscriptionUrl={subscriptionUrl}
                  explorerBehavioralFlags={explorerBehavioralFlags}
                  onCloseSubscriptionResponses={() => {
                    subscription.clearSubscription();
                    setShouldShowSubscriptionResponses(false);
                  }}
                />
              </SplitPane>
            ) : (
              <ResponsePane
                lastResult={lastResult}
                isTableMode={isTableMode}
                responseName={responseOperationName}
                explorerBehavioralFlags={explorerBehavioralFlags}
              />
            ))}
        </CursorProvider>
        {resultsTab === 'Response Headers' && lastResult && (
          <>
            <div className="py-2 text-sm text-secondary">
              Explorer is only able to show CORS-safelisted response headers by
              default. To see additional headers, please configure your endpoint
              to list them in{' '}
              <Link
                isExternal
                to="https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Access-Control-Expose-Headers"
              >
                Access-Control-Expose-Headers
              </Link>
            </div>
            <ReadonlyEditor
              language="json"
              src={JSON.stringify(
                lastResult.type === 'response'
                  ? isMinLengthArray(2, lastResult.data.headers)
                    ? lastResult.data.headers
                    : lastResult.data.headers[0]
                  : omit(lastResult, 'type'),
                null,
                '  ',
              )}
            />
          </>
        )}
        {resultsTab === 'Traces' &&
          lastResult?.type === 'response' &&
          (lastResult.data.trace ? (
            <div className="mt-3 overflow-y-auto p-2 text-primary">
              <TraceProtobuf
                trace={lastResult.data.trace}
                operationName={lastResult.operationName ?? 'Operation'}
                rowHoverColor={themedValue({
                  light: undefined,
                  dark: colors.blilet.base,
                })}
              />
            </div>
          ) : (
            <Alert status="warning" className="mt-5">
              {isMockingResponses
                ? 'Current response is mocked, traces only support server response'
                : lastResult.data.trace === null
                  ? 'Make sure your server is configured to send inline trace, see explorer settings for details'
                  : 'Please rerun your operation to get traces'}
            </Alert>
          ))}
        {resultsTab === 'Query Plan Preview' && (
          <QueryPlan
            mermaidData={queryPlanSVGText}
            currentResult={queryPlanResponseFromEditor}
            showMermaidQueryPlan={showMermaidQueryPlan}
            subgraphMap={subgraphMap}
            isPreview
          />
        )}
        {resultsTab === 'Query Plan' &&
          lastResult?.type === 'response' &&
          lastResult.data.queryPlan && (
            <QueryPlanRenderer
              mermaidData={queryPlanSVGText}
              showMermaidQueryPlan={showMermaidQueryPlan}
              result={lastResult.data.queryPlan}
              subgraphMap={subgraphMap}
            />
          )}
        {resultsTab === 'Connectors Debugger' &&
          displayedTabs.includes('Connectors Debugger') &&
          !!connectorsDebuggingContent?.data?.length && (
            <ConnectorsResponsePane
              version={connectorsDebuggingContent.version}
              data={connectorsDebuggingContent.data}
            />
          )}
      </div>
    </div>
  );
};
